const ExcelJS = require("exceljs");
const dayjs = require("dayjs");
const { getCheckPrice, normaliseCheckType } = require("./checkPricing");

const safeSheetName = (name) =>
  (name || "Sheet")
    .toString()
    .replace(/[\\/?*[\]:]/g, "_")
    .slice(0, 31);

const clean = (v) => (v ?? "").toString().trim();

const addStatementTable = (workbook, sheetName, headers, rows) => {
  const sheet = workbook.addWorksheet(safeSheetName(sheetName));
  sheet.addTable({
    name: `StatementTable${workbook.worksheets.length}`,
    ref: "A1",
    headerRow: true,
    style: { theme: null, showRowStripes: false },
    columns: headers.map((name) => ({ name, filterButton: true })),
    rows,
  });
  sheet.eachRow((row, rowNumber) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { name: "Calibri", size: 11, color: { argb: rowNumber === 1 ? "FFFFFFFF" : "FF081E56" }, bold: rowNumber === 1 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: rowNumber === 1 ? "FF081E56" : rowNumber % 2 === 0 ? "FFE6E9EF" : "FFFFFFFF" } };
    });
  });
  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.getRow(1).height = 32;
  sheet.getRow(1).alignment = { vertical: "middle", wrapText: true };
  headers.forEach((header, index) => {
    const column = sheet.getColumn(index + 1);
    column.width = Math.min(48, rows.reduce(
      (width, row) => Math.max(width, String(row[index] ?? "").length + 2),
      Math.max(16, header.length + 3),
    ));
    if (header === "Price" || header.endsWith("(£)")) {
      column.numFmt = '"£"#,##0.00';
    }
  });
  return sheet;
};

const asIso = (v) =>
  typeof v === "string"
    ? v
    : v?.toDate
      ? v.toDate().toISOString()
      : v instanceof Date
        ? v.toISOString()
        : "";

const parseDobToDDMMYYYY = (v) => {
  if (!v) return "";
  const s = typeof v === "string" ? v : asIso(v);

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = dayjs(s);
  return d.isValid() ? d.format("DD/MM/YYYY") : "";
};


  // 1) ONLY show forAdviser email (blank unless present) - used for main sheet "Requested For"
  const getForAdviserEmailOnly = (r) => {
    const fa = r?.forAdviser;
    if (!fa) return "";

    if (typeof fa === "string") return fa.trim();

    if (typeof fa === "object") {
      return fa.email || fa.adviserEmail || fa.userEmail || fa.workEmail || "";
    }

    return "";
  };

  // 2) Summary key: forAdviser first, else owner
  const getSummaryKeyEmail = (r) => {
    return getForAdviserEmailOnly(r) || clean(r?.__ownerEmail);
  };

  // 3) Name from email (best-effort)
  const nameFromEmail = (email) => {
    const e = clean(email).toLowerCase();
    if (!e || !e.includes("@")) return "";

    const local = e.split("@")[0];
    const tokens = local
      .replace(/[0-9]+/g, " ")
      .replace(/[._-]+/g, " ")
      .split(" ")
      .map((t) => t.trim())
      .filter(Boolean);

    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
    return tokens.map(cap).join(" ");
  };

  const isSimpleOrPepsCheck = (r) => {
    const t = (r.checkType || "").toString().toLowerCase();
    return t.includes("simple") || t.includes("pep");
  };

  // Default row mapping (Main sheet)
  const defaultMapRow = (d) => {
    const rd = d.__requestData || {};
    const p = rd.personal || {};

    const typeNorm = normaliseCheckType(d.checkType);
    const price = getCheckPrice(typeNorm);

    const summaryEmail = getSummaryKeyEmail(d);

    return {
      Name: nameFromEmail(summaryEmail), // forAdviser first else owner
      "Requested By": clean(d.__ownerEmail),
      "Requested For": getForAdviserEmailOnly(d), // only show if forAdviser exists

      "Client First Name": clean(p.firstName),
      "Client Last Name": clean(p.lastName),
      "Client DOB": parseDobToDDMMYYYY(p.dob),

      "Check Type": typeNorm,
      Date: dayjs(d.createdAtIso).isValid()
        ? dayjs(d.createdAtIso).format("DD/MM/YYYY")
        : "",

      Price: price,
    };
  };


function buildStatementWorkbook(enriched, { month, splitByType = false, mapRow } = {}) {
      const wb = new ExcelJS.Workbook();
      const rowMapper = mapRow || defaultMapRow;
      const addDetailsSheet = (name, records) => {
        const mapped = records.map(rowMapper);
        const headers = mapped.length ? Array.from(new Set(mapped.flatMap(Object.keys))) : Object.keys(defaultMapRow({}));
        addStatementTable(wb, name, headers,
          mapped.map((row) => headers.map((header) => row[header] ?? "")));
      };

      if (splitByType) {
        const byType = enriched.reduce((acc, r) => {
          const k = (r.checkType || "Unknown").toString();
          (acc[k] ||= []).push(r);
          return acc;
        }, {});

        Object.entries(byType).forEach(([type, arr]) => {
          addDetailsSheet(type, arr);
        });
      } else {
        addDetailsSheet(dayjs(month).format("MMMM YYYY"), enriched);
      }

      // ======================
      // SUMMARY TAB (pivot)
      // ======================

      const emails = Array.from(
        new Set(enriched.map((r) => getSummaryKeyEmail(r) || "Unknown")),
      ).sort((a, b) => a.localeCompare(b));

      const types = Array.from(
        new Set(
          enriched.map((r) => (r.__checkTypeNorm || "UNKNOWN").toString()),
        ),
      ).sort((a, b) => a.localeCompare(b));

      const counts = {};
      const money = {};

      for (const r of enriched) {
        const email = getSummaryKeyEmail(r) || "Unknown";
        const type = (r.__checkTypeNorm || "UNKNOWN").toString();
        const price = Number(r.__price ?? 0);

        counts[email] ??= {};
        money[email] ??= {};

        counts[email][type] = (counts[email][type] ?? 0) + 1;
        money[email][type] = (money[email][type] ?? 0) + price;
      }

      const header = [
        "Name", // NEW
        "User Email",
        ...types.map((t) => `${t} (Count)`),
        "Total (Count)",
        ...types.map((t) => `${t} (£)`),
        "Total (£)",
      ];

      const aoa = [header];

      for (const email of emails) {
        let rowCountTotal = 0;
        const countPart = [];

        for (const type of types) {
          const n = counts[email]?.[type] ?? 0;
          countPart.push(n);
          rowCountTotal += n;
        }

        let rowMoneyTotal = 0;
        const moneyPart = [];

        for (const type of types) {
          const amt = money[email]?.[type] ?? 0;
          moneyPart.push(amt);
          rowMoneyTotal += amt;
        }

        aoa.push([
          nameFromEmail(email), // NEW
          email,
          ...countPart,
          rowCountTotal,
          ...moneyPart,
          rowMoneyTotal,
        ]);
      }

      // Grand total row
      const grandCounts = [];
      let grandCountTotal = 0;

      for (const type of types) {
        const colSum = emails.reduce(
          (sum, email) => sum + (counts[email]?.[type] ?? 0),
          0,
        );
        grandCounts.push(colSum);
        grandCountTotal += colSum;
      }

      const grandMoney = [];
      let grandMoneyTotal = 0;

      for (const type of types) {
        const colMoney = emails.reduce(
          (sum, email) => sum + (money[email]?.[type] ?? 0),
          0,
        );
        grandMoney.push(colMoney);
        grandMoneyTotal += colMoney;
      }

      aoa.push([
        "Grand Total",
        "",
        ...grandCounts,
        grandCountTotal,
        ...grandMoney,
        grandMoneyTotal,
      ]);

      // Keep the grand total outside the table so sorting does not mix it with advisers.
      const pivotWs = addStatementTable(wb, "Summary", header, aoa.slice(1, -1));
      const totalRow = pivotWs.addRow(aoa[aoa.length - 1]);
      totalRow.font = { bold: true, color: { argb: "FF081E56" } };
      totalRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE6E9EF" } };
      });


return wb;
}
module.exports = { buildStatementWorkbook, getSummaryKeyEmail, isSimpleOrPepsCheck };
